import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ticket,
  Copy,
  Check,
  Clock,
  Percent,
  Truck,
  Gift,
  Tag,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface Coupon {
  _id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'flat' | 'free_shipping';
  discountValue: number;
  minPurchase: number;
  maxDiscount?: number;
  expiryDate: string;
  isUsed: boolean;
  category?: string;
  terms?: string[];
}

const UserCoupons: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'available' | 'used' | 'expired'>('available');
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    fetchCoupons();
  }, [activeTab]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setCoupons([]);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/coupons/my-coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        // Token expired, clear and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      
      const result = await response.json();
      const allCoupons: Coupon[] = result.success ? result.data || [] : [];
      
      // Filter based on tab
      let filtered = allCoupons;
      if (activeTab === 'available') {
        filtered = allCoupons.filter(c => !c.isUsed && new Date(c.expiryDate) > new Date());
      } else if (activeTab === 'used') {
        filtered = allCoupons.filter(c => c.isUsed);
      } else if (activeTab === 'expired') {
        filtered = allCoupons.filter(c => !c.isUsed && new Date(c.expiryDate) <= new Date());
      }

      setCoupons(filtered);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getDiscountLabel = (coupon: Coupon) => {
    switch (coupon.discountType) {
      case 'percentage':
        return `${coupon.discountValue}% OFF`;
      case 'flat':
        return `₹${coupon.discountValue} OFF`;
      case 'free_shipping':
        return 'FREE SHIPPING';
      default:
        return '';
    }
  };

  const getDiscountIcon = (type: string) => {
    switch (type) {
      case 'percentage':
        return <Percent className="w-6 h-6" />;
      case 'flat':
        return <Tag className="w-6 h-6" />;
      case 'free_shipping':
        return <Truck className="w-6 h-6" />;
      default:
        return <Gift className="w-6 h-6" />;
    }
  };

  const getDaysRemaining = (expiryDate: string) => {
    const days = Math.ceil((new Date(expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days === 0) return 'Expires today';
    if (days === 1) return 'Expires tomorrow';
    return `${days} days left`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Coupons</h1>
        <p className="text-gray-600">Apply coupons to get discounts on your orders</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-sm text-white/80">Available</p>
          <p className="text-2xl font-bold">
            {coupons.filter(c => !c.isUsed && new Date(c.expiryDate) > new Date()).length}
          </p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-sm text-white/80">Used</p>
          <p className="text-2xl font-bold">
            {coupons.filter(c => c.isUsed).length}
          </p>
        </div>
        <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl p-4 text-white">
          <p className="text-sm text-white/80">Expired</p>
          <p className="text-2xl font-bold">
            {coupons.filter(c => !c.isUsed && new Date(c.expiryDate) <= new Date()).length}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="flex border-b">
          {(['available', 'used', 'expired'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-4 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Coupons List */}
      <div className="space-y-4">
        {coupons.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab} coupons
            </h3>
            <p className="text-gray-600">
              {activeTab === 'available' 
                ? 'Check back later for new offers!'
                : activeTab === 'used'
                ? 'You haven\'t used any coupons yet.'
                : 'No expired coupons.'}
            </p>
          </div>
        ) : (
          coupons.map((coupon) => (
            <div
              key={coupon._id}
              className={`bg-white rounded-xl shadow-sm overflow-hidden ${
                coupon.isUsed || new Date(coupon.expiryDate) <= new Date()
                  ? 'opacity-60'
                  : ''
              }`}
            >
              <div className="flex">
                {/* Left - Discount Badge */}
                <div className={`w-32 flex flex-col items-center justify-center p-4 ${
                  coupon.discountType === 'percentage' 
                    ? 'bg-red-50' 
                    : coupon.discountType === 'flat'
                    ? 'bg-green-50'
                    : 'bg-blue-50'
                }`}>
                  <div className={`p-3 rounded-full mb-2 ${
                    coupon.discountType === 'percentage' 
                      ? 'bg-red-100 text-red-600' 
                      : coupon.discountType === 'flat'
                      ? 'bg-green-100 text-green-600'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {getDiscountIcon(coupon.discountType)}
                  </div>
                  <p className={`text-sm font-bold ${
                    coupon.discountType === 'percentage' 
                      ? 'text-red-600' 
                      : coupon.discountType === 'flat'
                      ? 'text-green-600'
                      : 'text-blue-600'
                  }`}>
                    {getDiscountLabel(coupon)}
                  </p>
                </div>

                {/* Right - Details */}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">
                        {coupon.description}
                      </p>
                      {coupon.category && (
                        <p className="text-sm text-gray-500 mb-2">
                          Valid on: {coupon.category}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">
                        Min. purchase: ₹{coupon.minPurchase}
                        {coupon.maxDiscount && ` • Max discount: ₹${coupon.maxDiscount}`}
                      </p>
                    </div>

                    {/* Copy Button or Status */}
                    {coupon.isUsed ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        <Check className="w-4 h-4 mr-1" />
                        Used
                      </span>
                    ) : new Date(coupon.expiryDate) <= new Date() ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Expired
                      </span>
                    ) : (
                      <button
                        onClick={() => copyToClipboard(coupon.code)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                          copiedCode === coupon.code
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {copiedCode === coupon.code ? (
                          <>
                            <Check className="w-4 h-4" />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            <span>{coupon.code}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Terms & Expiry */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-1" />
                        {getDaysRemaining(coupon.expiryDate)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expires: {new Date(coupon.expiryDate).toLocaleDateString()}
                      </div>
                    </div>
                    {coupon.terms && coupon.terms.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">
                          {coupon.terms.join(' • ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* How to Use */}
      <div className="mt-8 bg-blue-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">How to use coupons</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-medium text-blue-900">Copy Code</p>
              <p className="text-sm text-blue-700">Click on the coupon code to copy it</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-medium text-blue-900">Shop Products</p>
              <p className="text-sm text-blue-700">Add products to your cart</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-medium text-blue-900">Apply at Checkout</p>
              <p className="text-sm text-blue-700">Paste the code during checkout</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCoupons;
